// The colours categories are shown in. A group's subcategories are shades of the group's colour
// (the owner, 29.09.2026: "bills are blue, so their subcategories should be shades of blue; in
// Budgets too, so it is the same everywhere"); a category outside a group keeps its own colour.
// The shade follows the category's place among its group's subcategories, so it is the same on
// every screen. Stored colours stay untouched: moved out of its group, a category is its own again.

type Coloured = { id: string; color: string | null; groupId: string | null; sortOrder: number; archivedAt: string | null };
type Group = { id: string; color: string | null };

/** A group's colour: its own, else its first subcategory's (a group made from one category). */
export function withGroupColors<G extends Group>(groups: readonly G[], categories: readonly Coloured[]): G[] {
  return groups.map((group) => {
    if (group.color) return group;
    const first = siblings(categories, group.id)[0];
    return first?.color ? { ...group, color: first.color } : group;
  });
}

/** The categories with the colour to show: in a group, the group's shade for their place in it. */
export function withGroupShades<C extends Coloured>(categories: readonly C[], groups: readonly Group[]): C[] {
  const colorOf = new Map(withGroupColors(groups, categories).map((g) => [g.id, g.color]));
  const places = new Map<string, { index: number; count: number }>();
  for (const group of groups) {
    const children = siblings(categories, group.id);
    children.forEach((c, index) => places.set(c.id, { index, count: children.length }));
  }
  return categories.map((category) => {
    const base = category.groupId ? colorOf.get(category.groupId) : undefined;
    const place = places.get(category.id);
    if (!base || !place) return category;
    return { ...category, color: groupShade(base, place.index, place.count) };
  });
}

/**
 * Shade `index` of `count` of a colour: from darker to lighter with the hue turning a little,
 * at the colour's full chroma and never pale (the owner: "juicy, not pastel"): the lightest
 * stays under 0.8, so a light colour's shades go darker instead.
 */
export function groupShade(base: string, index: number, count: number): string {
  if (count <= 1 || !/^#[0-9a-f]{6}$/i.test(base)) return base;
  const t = index / (count - 1);
  const [l, c, h] = toOklch(base);
  const from = Math.max(0.36, Math.min(l - 0.12, 0.8 - 0.22));
  return fromOklch(from + 0.22 * t, c * 1.05, h + (t - 0.5) * 20);
}

function siblings(categories: readonly Coloured[], groupId: string): Coloured[] {
  return categories
    .filter((c) => c.groupId === groupId && c.archivedAt === null)
    .sort((a, b) => a.sortOrder - b.sortOrder || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

// OKLCH (Björn Ottosson's OKLab) — lightness steps that look even, unlike HSL's.

const toLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
const toGamma = (v: number) => (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055);

function toOklch(hex: string): [number, number, number] {
  const [r, g, b] = [1, 3, 5].map((i) => toLinear(parseInt(hex.slice(i, i + 2), 16) / 255)) as [number, number, number];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return [L, Math.hypot(A, B), (Math.atan2(B, A) * 180) / Math.PI];
}

function fromOklch(L: number, C: number, H: number): string {
  // Less chroma until the colour fits in sRGB: the lightness and hue stay as asked.
  for (let chroma = C; ; chroma = Math.max(0, chroma - 0.005)) {
    const rgb = oklabToRgb(L, chroma * Math.cos((H * Math.PI) / 180), chroma * Math.sin((H * Math.PI) / 180));
    if (chroma === 0 || rgb.every((v) => v >= -0.0005 && v <= 1.0005)) {
      return `#${rgb.map((v) => Math.round(Math.min(1, Math.max(0, toGamma(v))) * 255).toString(16).padStart(2, '0')).join('')}`;
    }
  }
}

function oklabToRgb(L: number, A: number, B: number): [number, number, number] {
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}
